import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Tooltip,
  Button,
  Menu,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import moment from 'moment';
import { ThemeContext } from 'src/store/ContextStore';
import { NOTE_TYPE_COLOURS } from 'src/components/Constants';
import {
  getRecordDate,
  getSearchableText,
  readRecordDate
} from './forms/formService';
import RecordCard from './RecordCard';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const NOTHING_IS_OPENABLE = () => false;

// TGCLOUD-1669 Phase 4 — the record browser, separated from the dialog it used
// to be welded into.
//
// Nothing about its behaviour changed in this extraction. What changed is that
// it no longer assumes it is inside a Dialog, so Phase 5 can dock it into a
// 348px column beside the sections instead of opening it over them. A docked
// column cannot afford a 215px facet rail, hence `facetLayout`.
//
// The dialog keeps three things that are genuinely its own: the patient label
// in its title bar, the sort control in its header, and printing. Printing
// needs the browser's filtered list and expanded set, so the browser reports
// those upward through `onVisibleChange` rather than the dialog reaching in.

const SidebarItem = ({ label, count, dotColor, isSelected, onClick, chip }) =>
  chip ? (
    // Compact inline form for `facetLayout="strip"`. A docked 348px column has
    // no room for a 215px rail, but the facets themselves still have to be
    // reachable, so they become a wrapping row of pills.
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        height: 22,
        px: 1,
        borderRadius: '11px',
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        backgroundColor: isSelected ? 'action.selected' : 'transparent',
        cursor: 'pointer',
        fontSize: '0.75rem',
        fontWeight: isSelected ? 700 : 500,
        whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: 'action.hover' }
      }}
    >
      {dotColor !== undefined && (
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: dotColor,
            flexShrink: 0
          }}
        />
      )}
      {label}
      {count != null && (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {count}
        </Box>
      )}
    </Box>
  ) : (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        cursor: 'pointer',
        backgroundColor: isSelected ? 'action.selected' : 'transparent',
        '&:hover': { backgroundColor: 'action.hover' }
      }}
    >
      {dotColor !== undefined && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: dotColor,
            flexShrink: 0
          }}
        />
      )}
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          fontWeight: isSelected ? 'bold' : 'normal',
          fontSize: '0.875rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </Typography>
      {count != null && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {count}
        </Typography>
      )}
    </Box>
  );

export default function RecordBrowser({
  tiles = [],
  isLoading,
  dateFormat: dateFormatProp,
  staffMemberOptions = [],
  // The member whose records these are — only used to resolve a PRENATAL event's subject label
  // (decision 21), so a fetal test never reads as something done to the patient.
  carrier,
  personLabel,
  // Offered both in the "Add record" menu and, for a person with nothing
  // recorded yet, as the empty-state prompt.
  onCreateRecord,
  creatableTypes = [],
  starterTypes,
  onOpenRecord,
  canOpenRecord = NOTHING_IS_OPENABLE,
  // Whether the browser is the thing the user is looking at. Drives the filter
  // reset and whether the window-level key handling is bound. In the dialog
  // this is the dialog's `open`; docked, it is simply true.
  active = true,
  keyboardSuspended = false,
  // 'rail' is the 215px left column the dialog uses. 'strip' is the compact
  // horizontal form a docked 348px column needs.
  facetLayout = 'rail',
  // Owned by the caller, because in the dialog the control for it lives in the
  // dialog's own header.
  sortOrder = 'newest',
  // Reports { filteredRecords, expandedIds } whenever either changes, so a
  // caller can print exactly what is on screen.
  onVisibleChange
}) {
  const t = useAutoTranslation();

  // The accent tints the "All records" dot, and the date format is a tenant
  // preference. Both are read here rather than required as props so the browser
  // works wherever it is mounted; `dateFormat` stays overridable because the
  // dialog already resolved it for its own title.
  const { theme } = React.useContext(ThemeContext);
  const dateFormat = dateFormatProp || theme?.dateFormat || 'DD/MM/YYYY';

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [periodFilter, setPeriodFilter] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [addAnchor, setAddAnchor] = useState(null);
  const emptyStateTypes = starterTypes ?? creatableTypes;

  const searchRef = useRef(null);
  const cardRefs = useRef({});

  useEffect(() => {
    if (active) {
      setSearchQuery('');
      setTypeFilter(null);
      setPeriodFilter(null);
      setExpandedIds(new Set());
      setFocusedIndex(0);
    }
  }, [active]);

  const typeCounts = useMemo(() => {
    const counts = {};
    tiles.forEach((t) => {
      counts[t.noteType] = (counts[t.noteType] || 0) + 1;
    });
    return counts;
  }, [tiles]);

  const { years, minYear, hasUndatedRecords } = useMemo(() => {
    const yearSet = new Set();
    let hasUndated = false;
    // The year of the day the record is dated (readRecordDate), so an imported
    // 1 January is filed under its own year at a negative offset too.
    tiles.forEach((t) => {
      const day = readRecordDate(t);
      if (day) {
        yearSet.add(day.getFullYear());
      } else {
        hasUndated = true;
      }
    });
    const sorted = Array.from(yearSet).sort((a, b) => b - a);
    return {
      years: sorted,
      minYear: sorted.length ? sorted[sorted.length - 1] : null,
      hasUndatedRecords: hasUndated
    };
  }, [tiles]);

  const filteredRecords = useMemo(() => {
    let result = [...tiles];

    if (typeFilter) {
      result = result.filter((r) => r.noteType === typeFilter);
    }

    if (periodFilter !== null) {
      if (periodFilter === 'undated') {
        result = result.filter((r) => !readRecordDate(r));
      } else {
        const year = parseInt(periodFilter, 10);
        result = result.filter(
          (r) => readRecordDate(r)?.getFullYear() === year
        );
      }
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((r) => getSearchableText(r).includes(q));
    }

    result.sort((a, b) => {
      const da = getRecordDate(a);
      const db = getRecordDate(b);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      const diff = moment(da).valueOf() - moment(db).valueOf();
      return sortOrder === 'newest' ? -diff : diff;
    });

    return result;
  }, [tiles, typeFilter, periodFilter, searchQuery, sortOrder]);

  useEffect(() => {
    if (focusedIndex >= filteredRecords.length && filteredRecords.length > 0) {
      setFocusedIndex(filteredRecords.length - 1);
    }
  }, [filteredRecords.length, focusedIndex]);

  useEffect(() => {
    const el = cardRefs.current[focusedIndex];
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusedIndex]);

  const toggleExpand = useCallback((id, e) => {
    const isShift = e?.shiftKey;
    setExpandedIds((prev) => {
      if (isShift) {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }
      return prev.has(id) ? new Set() : new Set([id]);
    });
  }, []);

  useEffect(() => {
    if (!active || keyboardSuspended) return;

    const handleKeyDown = (e) => {
      const el = document.activeElement;
      const tag = el?.tagName;
      const inInput =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        el?.isContentEditable ||
        !!el?.closest?.('form');

      if (e.key === 'ArrowDown' && !inInput) {
        e.preventDefault();
        const next = Math.min(focusedIndex + 1, filteredRecords.length - 1);
        setFocusedIndex(next);
        const record = filteredRecords[next];
        if (record) setExpandedIds(new Set([record._id]));
      } else if (e.key === 'ArrowUp' && !inInput) {
        e.preventDefault();
        const prev = Math.max(focusedIndex - 1, 0);
        setFocusedIndex(prev);
        const record = filteredRecords[prev];
        if (record) setExpandedIds(new Set([record._id]));
      } else if ((e.key === 'Enter' || e.key === ' ') && !inInput) {
        const record = filteredRecords[focusedIndex];
        if (record) {
          e.preventDefault();
          toggleExpand(record._id, e);
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [active, keyboardSuspended, focusedIndex, filteredRecords, toggleExpand]);

  const handlePrevNext = (direction) => {
    const next =
      direction === 'prev'
        ? Math.max(focusedIndex - 1, 0)
        : Math.min(focusedIndex + 1, filteredRecords.length - 1);
    setFocusedIndex(next);
    const record = filteredRecords[next];
    if (record) setExpandedIds(new Set([record._id]));
  };

  // Reported rather than lifted: the dialog only needs this to print, and
  // making it state up there would re-render the whole dialog on every keypress
  // in the search box.
  useEffect(() => {
    onVisibleChange?.({ filteredRecords, expandedIds });
  }, [filteredRecords, expandedIds, onVisibleChange]);

  const isStrip = facetLayout === 'strip';

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isStrip ? 'column' : 'row',
          flex: 1,
          overflow: 'hidden'
        }}
      >
        {/* FACETS — a 215px rail in the dialog, a wrapping strip when docked */}
        <Box
          sx={
            isStrip
              ? {
                  flexShrink: 0,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  overflow: 'auto',
                  maxHeight: 168,
                  p: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 0.5
                }
              : {
                  width: 215,
                  flexShrink: 0,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  overflow: 'auto',
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25
                }
          }
        >
          <TextField
            inputRef={searchRef}
            size="small"
            placeholder={t('Search records…')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              },

              htmlInput: { 'aria-label': t('Search records') }
            }}
          />

          {onCreateRecord && creatableTypes.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                fullWidth={!isStrip}
                startIcon={<AddIcon />}
                aria-haspopup="menu"
                aria-expanded={Boolean(addAnchor)}
                onClick={(e) => setAddAnchor(e.currentTarget)}
              >
                {t('Add record')}
              </Button>
              <Menu
                anchorEl={addAnchor}
                open={Boolean(addAnchor)}
                onClose={() => setAddAnchor(null)}
                slotProps={{ list: { dense: true } }}
              >
                {creatableTypes.map((type) => (
                  <MenuItem
                    key={type}
                    onClick={() => {
                      setAddAnchor(null);
                      onCreateRecord(type);
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        mr: 1,
                        flexShrink: 0,
                        backgroundColor: NOTE_TYPE_COLOURS[type] || '#888'
                      }}
                    />
                    {t(type)}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
              pt: 0.5,
              pb: 0.25
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}
            >
              {t('TYPE')}
            </Typography>
            <Tooltip
              title={
                filteredRecords.length > 0 &&
                filteredRecords.every((r) => expandedIds.has(r._id))
                  ? t('Collapse all')
                  : t('Expand all')
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={filteredRecords.length === 0}
                  aria-label={
                    filteredRecords.length > 0 &&
                    filteredRecords.every((r) => expandedIds.has(r._id))
                      ? t('Collapse all records')
                      : t('Expand all records')
                  }
                  onClick={() => {
                    const allExpanded =
                      filteredRecords.length > 0 &&
                      filteredRecords.every((r) => expandedIds.has(r._id));
                    setExpandedIds(
                      allExpanded
                        ? new Set()
                        : new Set(filteredRecords.map((r) => r._id))
                    );
                  }}
                >
                  {filteredRecords.length > 0 &&
                  filteredRecords.every((r) => expandedIds.has(r._id)) ? (
                    <UnfoldLessIcon fontSize="small" />
                  ) : (
                    <UnfoldMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <SidebarItem
            chip={isStrip}
            label={t('All records')}
            count={tiles.length}
            dotColor={typeFilter === null ? theme?.accent || '#1976d2' : '#bbb'}
            isSelected={typeFilter === null}
            onClick={() => setTypeFilter(null)}
          />
          {Object.entries(typeCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([type, count]) => (
              <SidebarItem
                chip={isStrip}
                key={type}
                label={t(type)}
                count={count}
                dotColor={NOTE_TYPE_COLOURS[type] || '#888'}
                isSelected={typeFilter === type}
                onClick={() => setTypeFilter(typeFilter === type ? null : type)}
              />
            ))}

          <Divider sx={{ my: 1 }} />

          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              color: 'text.secondary',
              fontSize: '0.875rem',
              px: 1,
              pt: 0.5,
              pb: 0.25
            }}
          >
            {t('PERIOD')}
          </Typography>

          <SidebarItem
            chip={isStrip}
            label={t('All time')}
            isSelected={periodFilter === null}
            onClick={() => setPeriodFilter(null)}
          />
          {years.map((year) => (
            <SidebarItem
              chip={isStrip}
              key={year}
              label={String(year)}
              isSelected={periodFilter === String(year)}
              onClick={() =>
                setPeriodFilter(
                  periodFilter === String(year) ? null : String(year)
                )
              }
            />
          ))}
          {hasUndatedRecords && minYear && (
            <SidebarItem
              chip={isStrip}
              label={t('Before {{minYear}}', { minYear })}
              isSelected={periodFilter === 'undated'}
              onClick={() =>
                setPeriodFilter(periodFilter === 'undated' ? null : 'undated')
              }
            />
          )}
        </Box>

        {/* RECORD LIST */}
        <Box
          role="list"
          aria-label={t('Clinical records list')}
          sx={{ flex: 1, overflow: 'auto', p: 2 }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 6,
                color: 'text.secondary'
              }}
            >
              <Typography>{t('Loading records…')}</Typography>
            </Box>
          ) : filteredRecords.length === 0 ? (
            // Two different empty states, because they mean different things.
            // "Nothing recorded for this person" is a clinical fact and the
            // next action is to record something. "Your filter excluded
            // everything" is a UI state and the next action is to clear it.
            // One shared "No records found." conflated the two.
            <Box sx={{ textAlign: 'center', pt: 6, color: 'text.secondary' }}>
              {tiles.length === 0 ? (
                <>
                  <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('Nothing recorded for this person yet')}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                    {t(
                      "Records added here appear on the pedigree and in the family's clinical history."
                    )}
                  </Typography>
                  {onCreateRecord && emptyStateTypes.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        justifyContent: 'center',
                        mt: 2.5
                      }}
                    >
                      {emptyStateTypes.map((type) => (
                        <Button
                          key={type}
                          size="small"
                          variant="outlined"
                          onClick={() => onCreateRecord(type)}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              mr: 1,
                              flexShrink: 0,
                              backgroundColor: NOTE_TYPE_COLOURS[type] || '#888'
                            }}
                          />
                          {t(type)}
                        </Button>
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Typography>
                  {t(
                    'No records match this filter. Clear the type, period or search to see the other'
                  )}{' '}
                  {tiles.length === 1
                    ? t('{{count}} record', { count: tiles.length })
                    : t('{{count}} records', { count: tiles.length })}
                  .
                </Typography>
              )}
            </Box>
          ) : (
            filteredRecords.map((record, index) => (
              <RecordCard
                key={record._id}
                record={record}
                isExpanded={expandedIds.has(record._id)}
                isFocused={focusedIndex === index}
                onToggle={toggleExpand}
                onFocus={() => setFocusedIndex(index)}
                cardRef={(el) => {
                  cardRefs.current[index] = el;
                }}
                dateFormat={dateFormat}
                staffMemberOptions={staffMemberOptions}
                carrier={carrier}
                personLabel={personLabel}
                onOpen={
                  onOpenRecord && canOpenRecord(record.noteType)
                    ? onOpenRecord
                    : undefined
                }
              />
            ))
          )}
        </Box>
      </Box>
      <Divider />
      {/* FOOTER */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 1,
          flexShrink: 0,
          gap: 1
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary'
          }}
        >
          {t('Showing')}{' '}
          {filteredRecords.length === 1
            ? t('{{count}} record', { count: filteredRecords.length })
            : t('{{count}} records', { count: filteredRecords.length })}
        </Typography>
        <Box
          sx={{
            flex: 1
          }}
        />
        <Button
          size="small"
          startIcon={<KeyboardArrowLeftIcon />}
          onClick={() => handlePrevNext('prev')}
          disabled={focusedIndex <= 0 || filteredRecords.length === 0}
        >
          {t('Prev')}
        </Button>
        <Button
          size="small"
          endIcon={<KeyboardArrowRightIcon />}
          onClick={() => handlePrevNext('next')}
          disabled={
            focusedIndex >= filteredRecords.length - 1 ||
            filteredRecords.length === 0
          }
        >
          {t('Next')}
        </Button>
      </Box>
    </>
  );
}
